import { useAddressInput, useComprehensiveTopNav, useCustomToast, usePagination, useSearch } from 'hooks';
import { routes } from 'lib/api';
import { Button, Image, LoadingIndicator, Pagination, SearchBar, Typography } from 'lib/components';
import { EmployeesDataTable } from 'lib/components/DataTables';
import { TextField, TimezoneSelect } from 'lib/components/FormFields';
import WorkingHourInputField from 'lib/components/WorkingHourInputField';
import { Day, DayIndex, DayItem, Time, WorkingHoursFactory } from 'lib/components/WorkingHourInputField/types';
import WorkingHourModal from 'lib/components/WorkingHourModal';
import { RateType } from 'lib/util';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectBuddyPunchLocationState,
	useSelectEmployees,
	useSelectHospital,
	useSelectHospitals,
	useSelectRequest,
} from 'store';
import { Hospital, HospitalWorkingHours } from 'types';
import classes from './index.module.scss';
import { BuddyPunchLocation } from '../../types/entity';
import BuddyPunchLocationSelect from '../../lib/components/FormFields/CustomSelect/BuddyPunchLocationSelect';

/**
 * Symbols for number inputs ($ for fixed amounts, % for percentages)
 */
const NUMBER_SYMBOL = ['$', '%'];

/**
 * Initial working hours configuration for all days of the week
 * Sets up default hours (8:00 AM to 8:00 PM) with isOperating=false
 * Used as a fallback if hospital doesn't have working hours defined
 */
const WORKING_HOURS_FACTORY_INIT = [
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
].reduce<WorkingHoursFactory>(
	(acc, cur) => {
		acc.days.push({
			day: cur as Day,
			isOperating: false,
			startTime: { hr: '08', min: '00', pm: false },
			endTime: { hr: '08', min: '00', pm: true },
		});
		return acc;
	},
	{ days: [], selectedIndex: 0 }
);

/**
 * Props interface for the HospitalEditScreenView component
 */
interface Props {
	/** Hospital data to be edited */
	hospital: Hospital;
}

/**
 * HospitalEditScreen component
 * Container component that fetches hospital data and passes it to the view component
 * Shows a loading indicator while data is being fetched
 *
 * @returns {JSX.Element} - Rendered component
 */
const HospitalEditScreen: React.FC = () => {
	// Get hospital ID from URL parameters
	const { id } = useParams<{ id: string }>();

	// Redux state and actions
	const dispatch = useAppDispatch();
	const hospitals = useSelectHospitals();
	const buddyPunchLocation = useSelectBuddyPunchLocationState();
	const getHospitalById = useSelectHospital();

	// Local state for the hospital data
	const [hospital, setHospital] = useState<Hospital>();

	/**
	 * Fetch hospital data and BuddyPunch locations on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchHospital(parseInt(id)));
		dispatch(actions.getBuddyPunchLocations());
	}, []);

	/**
	 * Update local hospital state when data changes in Redux store
	 * Monitors both hospitals and buddyPunchLocation for updates
	 */
	useEffect(() => {
		const hospital = getHospitalById(parseInt(id));

		if (hospital) {
			setHospital(hospital);
		}
	}, [hospitals.updatedAt, buddyPunchLocation.updatedAt]);

	// Show loading indicator until hospital data is available
	return hospital ? <HospitalEditScreenView hospital={hospital} /> : <LoadingIndicator />;
};

/**
 * HospitalEditScreenView component
 * Main view component for editing hospital details
 * Displays form with hospital information, working hours, and employee list
 *
 * @param {Props} props - Component props containing hospital data
 * @returns {JSX.Element} - Rendered component
 */
const HospitalEditScreenView = ({ hospital }: Props) => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Permission checks for employee-related actions
	const { canCreateEmployee, canListEmployee } = useSelectAuthorization();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Hospital and employee data from Redux store
	const hospitals = useSelectHospitals();
	const buddyPunchLocationState = useSelectBuddyPunchLocationState();
	const employees = useSelectEmployees();

	// Basic hospital information form state
	const [name, setName] = useState(hospital.name);
	const [timezone, setTimeZone] = useState<string | undefined>(hospital.timezone);
	const [email, setEmail] = useState(hospital.email);
	const [employeesAmount, setEmployeesAmount] = useState(hospital.employeesAmount ?? 0);
	const [phoneNumber, setPhoneNumber] = useState(hospital.phoneNumber ?? '');
	const [payoutId, setPayoutId] = useState(hospital.payoutId ?? '');

	// Pricing rates form state
	const [regularRate, setRegularRate] = useState(hospital.regularRate ?? 0);
	const [overtimeRate, setOvertimeRate] = useState(hospital.overtimeRate ?? 0);
	const [weekendRate, setWeekendRate] = useState(hospital.weekendRate ?? 0);
	const [holidayRate, setHolidayRate] = useState(hospital.holidayRate ?? 0);

	// Rate types (fixed amount or percentage)
	const [overtimeRateTypeId, setOvertimeRateTypeId] = useState<RateType>(hospital.overtimeRateTypeId ?? 0);
	const [weekendRateTypeId, setWeekendRateTypeId] = useState<RateType>(hospital.weekendRateTypeId ?? 0);
	const [holidayRateTypeId, setHolidayRateTypeId] = useState<RateType>(hospital.holidayRateTypeId ?? 0);

	// BuddyPunch integration - find matching location from list
	const [buddyPunchId, setBuddyPunchId] = useState<BuddyPunchLocation | undefined>(
		hospital.buddyPunchId !== undefined
			? buddyPunchLocationState.list.find(
					(element) => parseInt(element.id as unknown as string, 10) === parseInt(hospital.buddyPunchId!, 10)
			  )
			: undefined
	);

	// Cancellation policy percentages
	const [beforeDispatch, setBeforeDispatch] = useState<number>(hospital.cancellationPolicy.beforeDispatch);
	const [afterDispatch, setAfterDispatch] = useState<number>(hospital.cancellationPolicy.afterDispatch);
	const [beforeTreatment, setBeforeTreatment] = useState<number>(hospital.cancellationPolicy.beforeTreatment);
	const [duringTreatment, setDuringTreatment] = useState<number>(hospital.cancellationPolicy.duringTreatment);

	// Search query for filtering employees
	const [query, setQuery] = useState('');

	/**
	 * Fetch employees for this hospital on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchEmployees(hospital.id));
	}, []);

	/**
	 * Update BuddyPunch location when data changes in Redux store
	 * Only updates if buddyPunchId is undefined (not already set)
	 */
	React.useEffect(() => {
		if (buddyPunchId === undefined) {
			setBuddyPunchId(
				hospital.buddyPunchId !== undefined
					? buddyPunchLocationState.list.find(
							(element) =>
								parseInt(element.id as unknown as string, 10) === parseInt(hospital.buddyPunchId!, 10)
					  )
					: undefined
			);
		}
	}, [hospitals.updatedAt, buddyPunchLocationState.updatedAt]);

	/**
	 * Set up pagination for employees list
	 * Returns data and handlers for paginated display with 3 items per page
	 */
	const { isDataFetched, setData, listing, paginatedData, paginationProps } = usePagination(employees, {
		itemsPerPage: 3,
	});

	/**
	 * Set up search functionality for employees list
	 * Filters employees by name, email, or phone number
	 * First callback is executed when query is empty, second when query has content
	 */
	useSearch(
		query,
		() => {
			// When query is empty, show all employees
			setData(listing);
		},
		() => {
			// When query has content, filter employees
			const result = listing.filter(
				(employee) =>
					employee.name.toLowerCase().search(query.toLowerCase()) !== -1 ||
					employee.email.toLowerCase().search(query.toLowerCase()) !== -1 ||
					employee.phoneNumber.toLowerCase().search(query.toLowerCase()) !== -1
			);
			setData(result);
		}
	);

	/**
	 * Monitor API requests for hospital updates
	 * Shows toast notifications for success/failure
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.HOSPITAL_ID(hospital.id), 'PUT')) {
			if (RM.failed()) {
				// Show error message
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				// Show success message
				toast(active.message);
			}
		}
	}, [active]);

	// Working hours configuration state
	const [workingHoursFactory, setWorkingHoursFactory] = useState<WorkingHoursFactory>(WORKING_HOURS_FACTORY_INIT);
	// Controls visibility of the working hours modal
	const [workingHourModalOpen, setWorkingHourModalOpen] = useState(false);

	/**
	 * Navigate to the next day in the working hours modal
	 * Cycles back to Monday after Sunday
	 */
	const nextWorkingDay = () => {
		setWorkingHoursFactory((prev) => {
			const newIndex = prev.selectedIndex + 1 > 6 ? 0 : prev.selectedIndex + 1;
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: newIndex as DayIndex,
			};
		});
	};

	/**
	 * Navigate to the previous day in the working hours modal
	 * Cycles to Sunday after Monday
	 */
	const prevWorkingDay = () =>
		setWorkingHoursFactory((prev) => {
			const newIndex = prev.selectedIndex - 1 < 0 ? 6 : prev.selectedIndex - 1;
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: newIndex as DayIndex,
			};
		});

	/**
	 * Update working hours for a specific day when confirmed in the modal
	 *
	 * @param {DayItem} dayItem - The updated day configuration
	 */
	const handleWorkingHourModalOnConfirmed = (dayItem: DayItem) => {
		setWorkingHoursFactory((prev) => {
			// Find and update the specific day that was changed
			const newDays = [...prev.days].map((item) => {
				if (item.day === dayItem.day) {
					return { ...dayItem };
				}
				return item;
			});
			return {
				...prev,
				days: newDays,
			};
		});
	};

	/**
	 * Set the currently selected day in the working hours configuration
	 *
	 * @param {DayIndex} index - Index of the day to select (0-6)
	 */
	const setWorkingHoursDayIndex = (index: DayIndex) => {
		setWorkingHoursFactory((prev) => {
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: index,
			};
		});
	};

	/**
	 * Handle click on a working hour field to open the modal
	 * Sets the selected day to the clicked day
	 *
	 * @param {number} index - Index of the clicked day
	 */
	const handleWorkingHourFieldClick = (index: number) => {
		setWorkingHourModalOpen((prev) => {
			setWorkingHoursDayIndex(index as DayIndex);
			return !prev;
		});
	};

	/**
	 * Custom hook for address input fields
	 * Provides state and handlers for all address-related fields
	 * Pre-filled with hospital's existing address
	 */
	const {
		addressLine1,
		setAddressLine1,
		addressLine2,
		setAddressLine2,
		city,
		setCity,
		state,
		setState,
		postalCode,
		setPostalCode,
	} = useAddressInput(hospital.address);

	/**
	 * Parse time string into Time object
	 * Used to convert API time strings to the format needed for the working hours form
	 *
	 * @param {string | null} entry - Time string in format "hh:mm AM/PM" or null
	 * @param {boolean} defaultIsPM - Default AM/PM value if entry is null
	 * @returns {Time} - Time object with hour, minute, and AM/PM flag
	 */
	const parseDay = useCallback(
		(entry: string | null, defaultIsPM: boolean) => {
			if (entry) {
				// Parse time string using moment
				const time = moment.utc(entry, 'hh:mm A');
				return {
					hr: time.format('hh'),
					min: time.format('mm'),
					pm: entry.includes('PM'),
				};
			}

			// Return default values if entry is null
			return {
				hr: '09',
				min: '00',
				pm: defaultIsPM,
			};
		},
		[hospital.workingHours]
	);

	/**
	 * Initialize working hours from hospital data when workingHours changes
	 * Creates a WorkingHoursFactory object with parsed time data for each day
	 */
	useEffect(() => {
		const workingHours: WorkingHoursFactory = {
			days: [
				// Monday configuration
				{
					day: 'Monday',
					isOperating: hospital.workingHours.monFrom !== null && hospital.workingHours.monTo !== null,
					startTime: parseDay(hospital.workingHours.monFrom, false),
					endTime: parseDay(hospital.workingHours.monTo, true),
				},
				// Tuesday configuration
				{
					day: 'Tuesday',
					isOperating: hospital.workingHours.tueFrom !== null && hospital.workingHours.tueTo !== null,
					startTime: parseDay(hospital.workingHours.tueFrom, false),
					endTime: parseDay(hospital.workingHours.tueTo, true),
				},
				// Wednesday configuration
				{
					day: 'Wednesday',
					isOperating: hospital.workingHours.wedFrom !== null && hospital.workingHours.wedTo !== null,
					startTime: parseDay(hospital.workingHours.wedFrom, false),
					endTime: parseDay(hospital.workingHours.wedTo, true),
				},
				// Thursday configuration
				{
					day: 'Thursday',
					isOperating: hospital.workingHours.thuFrom !== null && hospital.workingHours.thuTo !== null,
					startTime: parseDay(hospital.workingHours.thuFrom, false),
					endTime: parseDay(hospital.workingHours.thuTo, true),
				},
				// Friday configuration
				{
					day: 'Friday',
					isOperating: hospital.workingHours.friFrom !== null && hospital.workingHours.friTo !== null,
					startTime: parseDay(hospital.workingHours.friFrom, false),
					endTime: parseDay(hospital.workingHours.friTo, true),
				},
				// Saturday configuration
				{
					day: 'Saturday',
					isOperating: hospital.workingHours.satFrom !== null && hospital.workingHours.satTo !== null,
					startTime: parseDay(hospital.workingHours.satFrom, false),
					endTime: parseDay(hospital.workingHours.satTo, true),
				},
				// Sunday configuration
				{
					day: 'Sunday',
					isOperating: hospital.workingHours.sunFrom !== null && hospital.workingHours.sunTo !== null,
					startTime: parseDay(hospital.workingHours.sunFrom, false),
					endTime: parseDay(hospital.workingHours.sunTo, true),
				},
			],
			// Maintain the currently selected day index
			selectedIndex: workingHoursFactory.selectedIndex,
		};

		// Update working hours state with parsed data
		setWorkingHoursFactory(workingHours);
	}, [hospital.workingHours]);

	/**
	 * Format time for working hours
	 * Converts Time object to string format or null if not operating
	 *
	 * @param {Time} entry - Time object with hour, minute, and AM/PM
	 * @param {boolean} isOperating - Whether the hospital is operating at this time
	 * @returns {string | null} - Formatted time string or null
	 */
	const formatDay = useCallback(
		(entry: Time, isOperating: boolean) => {
			return isOperating ? `${entry.hr}:${entry.min} ${entry.pm ? 'PM' : 'AM'}` : null;
		},
		[workingHoursFactory]
	);

	/**
	 * Update hospital with form data
	 * Formats working hours and prepares data for API request
	 */
	const updateHospital = () => {
		// Format working hours for all days of the week
		const workingHours: HospitalWorkingHours = {
			monFrom: formatDay(workingHoursFactory.days[0].startTime, workingHoursFactory.days[0].isOperating),
			monTo: formatDay(workingHoursFactory.days[0].endTime, workingHoursFactory.days[0].isOperating),
			tueFrom: formatDay(workingHoursFactory.days[1].startTime, workingHoursFactory.days[1].isOperating),
			tueTo: formatDay(workingHoursFactory.days[1].endTime, workingHoursFactory.days[1].isOperating),
			wedFrom: formatDay(workingHoursFactory.days[2].startTime, workingHoursFactory.days[2].isOperating),
			wedTo: formatDay(workingHoursFactory.days[2].endTime, workingHoursFactory.days[2].isOperating),
			thuFrom: formatDay(workingHoursFactory.days[3].startTime, workingHoursFactory.days[3].isOperating),
			thuTo: formatDay(workingHoursFactory.days[3].endTime, workingHoursFactory.days[3].isOperating),
			friFrom: formatDay(workingHoursFactory.days[4].startTime, workingHoursFactory.days[4].isOperating),
			friTo: formatDay(workingHoursFactory.days[4].endTime, workingHoursFactory.days[4].isOperating),
			satFrom: formatDay(workingHoursFactory.days[5].startTime, workingHoursFactory.days[5].isOperating),
			satTo: formatDay(workingHoursFactory.days[5].endTime, workingHoursFactory.days[5].isOperating),
			sunFrom: formatDay(workingHoursFactory.days[6].startTime, workingHoursFactory.days[6].isOperating),
			sunTo: formatDay(workingHoursFactory.days[6].endTime, workingHoursFactory.days[6].isOperating),
		};

		// Dispatch action to update hospital with all form data
		dispatch(
			actions.updateHospital(hospital.id, {
				// Basic information
				name,
				email,
				// Address information
				addressLine1,
				addressLine2: addressLine2 === '' ? undefined : addressLine2,
				city,
				state,
				postalCode,
				phoneNumber,
				// Integration IDs
				payoutId: payoutId !== '' ? payoutId : undefined,
				buddyPunchId: buddyPunchId !== undefined ? buddyPunchId.id.toString() : undefined,
				// Rate information
				regularRate,
				overtimeRate,
				weekendRate,
				holidayRate,
				overtimeRateTypeId,
				weekendRateTypeId,
				holidayRateTypeId,
				// Other details
				employeesAmount: employeesAmount !== 0 ? employeesAmount : undefined,
				timezone: timezone!,
				workingHours,
				// Cancellation policy
				cancellationPolicy: {
					beforeDispatch,
					afterDispatch,
					beforeTreatment,
					duringTreatment,
				},
			})
		);
	};

	/**
	 * Handle rate type symbol change ($ or %)
	 * Updates the appropriate rate type state based on the field
	 *
	 * @param {string} field - Which rate field is being updated
	 * @param {RateType} symbol - The new rate type (0 for fixed, 1 for percentage)
	 */
	const handleSymbolChange = (field: 'overtime' | 'weekend' | 'holiday', symbol: RateType) => {
		if (field === 'overtime') setOvertimeRateTypeId(symbol);
		else if (field === 'weekend') setWeekendRateTypeId(symbol);
		else setHolidayRateTypeId(symbol);
	};

	// Commented out employee selection functionality
	// Likely replaced with a different approach for employee management
	// const handleCheckAction = (id: number, isChecked: boolean) => {
	// 	if (isChecked && !checked.includes(id)) {
	// 		setChecked([id]);
	// 		return;
	// 	}

	// 	if (!isChecked || checked.includes(id)) {
	// 		setChecked([]);
	// 	}
	// };

	// const handleDelete = () => {
	// 	dispatch(actions.deleteEmployee(hospital.id, checked[0]));
	// };

	// Set up top navigation with hospital name and edit indicator
	useComprehensiveTopNav(`${name} - Edit`);

	// Show loading indicator while employee data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<>
			{/* Working hours modal - shown when a working hour field is clicked */}
			<WorkingHourModal
				isOpen={workingHourModalOpen}
				workingHoursFactory={workingHoursFactory}
				onClose={() => setWorkingHourModalOpen(false)}
				onNextDay={nextWorkingDay}
				onPrevDay={prevWorkingDay}
				onConfirmed={handleWorkingHourModalOnConfirmed}
			/>
			{/* Main hospital edit form container */}
			<div className={classes['editHospitalNew']}>
				{/* Hospital name and address section */}
				<div className={classes['editHospitalNew-section']}>
					<div
						className={
							classes['editHospitalNew-sectionFields'] +
							' ' +
							classes['editHospitalNew-spacingTextFields']
						}
					>
						<div className={classes['editHospitalNew-hospitalNameField']}>
							<TextField
								value={name}
								onChange={(e) => setName(e.target.value)}
								color="primary"
								variant="labelled"
								label="Hospital Name:"
							/>
						</div>
						<TimezoneSelect value={hospital.timezone} setValue={setTimeZone} />
						<TextField
							placeholder="Address 1"
							label="Address Line 1:"
							variant="labelled"
							value={addressLine1}
							onChange={(e) => setAddressLine1(e.target.value)}
						/>
						<TextField
							placeholder="Address 2"
							label="Address Line 2:"
							variant="labelled"
							value={addressLine2}
							onChange={(e) => setAddressLine2(e.target.value)}
						/>
						<TextField
							placeholder="Ohara"
							label="City:"
							variant="labelled"
							value={city}
							onChange={(e) => setCity(e.target.value)}
						/>
						<TextField
							placeholder="Texas"
							label="State:"
							variant="labelled"
							value={state}
							onChange={(e) => setState(e.target.value)}
						/>
						<TextField
							placeholder="00000"
							label="Postal Code:"
							variant="labelled"
							value={postalCode}
							onChange={(e) => setPostalCode(e.target.value)}
						/>
					</div>
				</div>
				<div className={classes['editHospitalNew-section']}>
					<Typography weight="bold" className={classes['editHospitalNew-sectionTitle']}>
						Basic Info
					</Typography>
					<div className={classes['editHospitalNew-sectionFields']}>
						<div className={classes['editHospitalNew-phoneField']}>
							<TextField
								value={phoneNumber}
								onChange={(e) => setPhoneNumber(e.target.value)}
								variant="labelled"
								label="Phone:"
								placeholder="+1 55 55 55 555"
							/>
						</div>
						<div className={classes['editHospitalNew-employeeField']}>
							<TextField
								value={employeesAmount}
								onChange={(e) => setEmployeesAmount(e.target.valueAsNumber)}
								variant="labelled"
								label="Employees:"
								type="number"
							/>
						</div>
						<div className={classes['editHospitalNew-emailField']}>
							<TextField
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								variant="labelled"
								type="email"
								label="Email:"
								placeholder="contact@example.com"
							/>
						</div>
						<div>
							<BuddyPunchLocationSelect value={buddyPunchId} setValue={setBuddyPunchId} />
						</div>
					</div>
				</div>
				<div className={classes['editHospitalNew-section']}>
					<Typography weight="bold" className={classes['editHospitalNew-sectionTitle']}>
						Cancellation Policy
					</Typography>
					<div className={classes['editHospitalNew-sectionFields']}>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Before Dispatch:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={beforeDispatch}
								onChange={(e) => setBeforeDispatch(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="After Dispatch:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={afterDispatch}
								onChange={(e) => setAfterDispatch(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Before Treatment:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={beforeTreatment}
								onChange={(e) => setBeforeTreatment(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="During Treatment:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={duringTreatment}
								onChange={(e) => setDuringTreatment(e.target.valueAsNumber)}
							/>
						</div>
					</div>
				</div>
				<div className={classes['editHospitalNew-section']}>
					<Typography weight="bold" className={classes['editHospitalNew-sectionTitle']}>
						Pricing Per Hour
					</Typography>
					<div className={classes['editHospitalNew-sectionFields']}>
						<div className={classes['editHospitalNew-quickbooksField']}>
							<TextField
								variant="labelled"
								label="Quickbooks ID"
								placeholder="123465798"
								value={payoutId}
								onChange={(e) => setPayoutId(e.target.value)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Pricing per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={regularRate}
								onChange={(e) => setRegularRate(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-overtimePerHourField']}>
							<TextField
								variant="labelled"
								label="Overtime per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={overtimeRate}
								defaultSymbolIndex={overtimeRateTypeId}
								onChange={(e) => setOvertimeRate(e.target.valueAsNumber)}
								onSymbolChange={(value) => handleSymbolChange('overtime', value)}
							/>
						</div>
						<div className={classes['editHospitalNew-weekendPerHourField']}>
							<TextField
								variant="labelled"
								label="Weekend per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={weekendRate}
								defaultSymbolIndex={weekendRateTypeId}
								onChange={(e) => setWeekendRate(e.target.valueAsNumber)}
								onSymbolChange={(value) => handleSymbolChange('weekend', value)}
							/>
						</div>
						<div className={classes['editHospitalNew-holidayPerHourField']}>
							<TextField
								variant="labelled"
								label="Holiday per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={holidayRate}
								defaultSymbolIndex={holidayRateTypeId}
								onChange={(e) => setHolidayRate(e.target.valueAsNumber)}
								onSymbolChange={(value) => handleSymbolChange('holiday', value)}
							/>
						</div>
					</div>
				</div>
				<div className={classes['editHospitalNew-section']}>
					<Typography weight="bold" className={classes['editHospitalNew-sectionTitle']}>
						Working Hours
					</Typography>
					<div className={classes['editHospitalNew-sectionFields']}>
						{workingHoursFactory.days.map((item, index) => (
							<div
								onClick={() => handleWorkingHourFieldClick(index)}
								className={classes['createHospitalNew-workingHoursField']}
								key={item.day}
								style={{ cursor: 'pointer' }}
							>
								<WorkingHourInputField
									day={item.day}
									start={item.startTime}
									end={item.endTime}
									onChange={(value) => {
										const workingHours = { ...workingHoursFactory };
										workingHours.days[index].startTime = value.start;
										workingHours.days[index].endTime = value.end;

										setWorkingHoursFactory(workingHours);
									}}
									disabled
									prefix
								/>
							</div>
						))}
					</div>
				</div>

				{canListEmployee && (
					<div className={classes['editHospitalNew-employees']}>
						<div className={classes['editHospitalNew-employeesTop']}>
							<Typography weight="bold">Employees:</Typography>
							<div className={classes['editHospitalNew-employeesTop-controls']}>
								<SearchBar
									query={query}
									setQuery={setQuery}
									className={classes['editHospitalNew-employeesTop-search']}
									placeholder="Search..."
									variant="outline"
								/>
								{canCreateEmployee && (
									<a
										className={classes['editHospitalNew-employeesTop-add']}
										onClick={() => push(`/employees/${hospital.id}/new`)}
									>
										<Typography>Add New Employee</Typography>
										<span>
											<Image src="icPlusPrimary" />
										</span>
									</a>
								)}
							</div>
							<EmployeesDataTable
								hospitalId={hospital.id}
								employees={paginatedData}
								checkAction={() => {}}
								checkedId={-1}
							/>
							<Pagination {...paginationProps} />
						</div>
					</div>
				)}
				<div className={classes['editHospitalNew-footer']}>
					<Button
						variant="standard"
						color="primary"
						title="Update Hospital"
						style={{
							marginRight: '1rem',
						}}
						onClick={updateHospital}
					/>
					<Button variant="standard" color="gray" title="Cancel" onClick={() => push('/hospitals')} />
				</div>
			</div>
		</>
	);
};

export default HospitalEditScreen;
